import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../utils/supabase'
import { Smartphone, ChevronLeft, ChevronRight, Loader2, Search, X, Download, Ban, RotateCcw, History, ListChecks, Plus } from 'lucide-react'
import Icon3D from '../components/Icon3D'

const LIMIT = 25

// Color del icono 3D por tipo de línea
const TYPE_ICON_COLOR = {
  toka: 'red', oui: 'yellow', telcel: 'sky', allce: 'purple', att: 'cyan', otras: 'slate',
}

const TYPE_CONFIG = {
  toka: {
    title: 'TOKA SIM Roja', color: 'red',
    cols: [
      { key: 'msisdn',      label: 'MSISDN',   mono: true  },
      { key: 'iccid',       label: 'ICCID',    mono: true, truncate: true },
      { key: 'client_name', label: 'Cliente'               },
      { key: 'plan',        label: 'Plan'                  },
      { key: 'status',      label: 'Estatus',  badge: true },
    ]
  },
  oui: {
    title: 'OUI (Líneas Amarillas)', color: 'yellow',
    cols: [
      { key: 'mdn',         label: 'MDN',      mono: true  },
      { key: 'iccid',       label: 'ICCID',    mono: true, truncate: true },
      { key: 'client_name', label: 'Cliente'               },
      { key: 'plan',        label: 'Plan'                  },
      { key: 'status',      label: 'Estatus',  badge: true },
      { key: 'cost',        label: 'Costo',    currency: true },
    ]
  },
  telcel: {
    title: 'Telcel B2B', color: 'blue',
    cols: [
      { key: 'phone',   label: 'Teléfono', mono: true },
      { key: 'carrier', label: 'Carrier'              },
      { key: 'amount',  label: 'Monto',    currency: true },
      { key: 'date',    label: 'Fecha'                },
    ]
  },
  allce: {
    title: 'ALLCE', color: 'purple',
    cols: [
      { key: 'iccid',       label: 'ICCID',    mono: true, truncate: true },
      { key: 'phone',       label: 'Teléfono', mono: true },
      { key: 'plan',        label: 'Plan'                  },
      { key: 'status',      label: 'Estatus',  badge: true },
      { key: 'activation_date', label: 'Fecha Activación' },
      { key: 'tracker_name', label: 'Tracker', badge: true },
      { key: 'tracker_client', label: 'Cliente Tracker' },
    ]
  },
  att: {
    title: 'AT&T', color: 'blue',
    cols: [
      { key: 'iccid',   label: 'ICCID',    mono: true, truncate: true },
      { key: 'msisdn',  label: 'MSISDN',   mono: true },
      { key: 'plan',    label: 'Plan'                  },
      { key: 'status',  label: 'Estado',   badge: true },
      { key: 'client_name', label: 'Cliente'           },
    ]
  },
  otras: {
    title: 'Otras / Sin Línea', color: 'gray',
    cols: [
      { key: 'label',       label: 'Unidad',   mono: false },
      { key: 'phone',       label: 'Teléfono', mono: true  },
      { key: 'client_name', label: 'Cliente'               },
      { key: 'line_type',   label: 'Tipo'                  },
      { key: 'connection_status', label: 'Estado', badge: true },
    ]
  },
}

const colorMap = {
  red:     { header: 'text-red-400',     badge: 'bg-red-500/20 text-red-400',       tab: 'text-red-400 border-red-400' },
  yellow:  { header: 'text-yellow-400',  badge: 'bg-yellow-500/20 text-yellow-400', tab: 'text-yellow-400 border-yellow-400' },
  blue:    { header: 'text-blue-400',    badge: 'bg-blue-500/20 text-blue-400',     tab: 'text-blue-400 border-blue-400' },
  purple:  { header: 'text-purple-400',  badge: 'bg-purple-500/20 text-purple-400', tab: 'text-purple-400 border-purple-400' },
  gray:    { header: 'text-gray-400',    badge: 'bg-gray-700 text-gray-400',        tab: 'text-gray-400 border-gray-400' },
}

function Cell({ col, value }) {
  if (value == null || value === '') return <span className="text-gray-600">—</span>

  if (col.currency) return (
    <span className="text-sm text-white">${Number(value).toFixed(2)}</span>
  )
  if (col.badge) {
    const active = String(value).toLowerCase().includes('activ')
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-700 text-gray-400'}`}>
        {value}
      </span>
    )
  }
  if (col.mono && col.truncate) return (
    <span className="font-mono text-xs text-gray-400 truncate block max-w-[180px]" title={value}>{value}</span>
  )
  if (col.mono) return <span className="font-mono text-sm text-white">{value}</span>
  return <span className="text-sm text-gray-300 truncate block max-w-[160px]">{value}</span>
}

function Pagination({ page, pages, total, setPage }) {
  if (pages <= 1) return null
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700 text-sm">
      <span className="text-gray-400">
        {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} de {total.toLocaleString()}
      </span>
      <div className="flex items-center gap-2">
        <button onClick={() => setPage(p => p - 1)} disabled={page === 0}
          className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-gray-400 min-w-[60px] text-center">{page + 1} / {pages}</span>
        <button onClick={() => setPage(p => p + 1)} disabled={page >= pages - 1}
          className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

function TelcelB2B() {
  const [tab, setTab] = useState('activas')
  const [lines, setLines]       = useState([])
  const [bajas, setBajas]       = useState([])
  const [loading, setLoading]   = useState(true)
  const [search, setSearch]     = useState('')
  const [page, setPage]         = useState(0)
  const [total, setTotal]       = useState(0)
  const [actionId, setActionId] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [saving, setSaving]   = useState(false)
  const [form, setForm] = useState({ phone: '', carrier: 'TELCEL', amount: '', date: '', client_name: '' })

  useEffect(() => { setPage(0); setSearch('') }, [tab])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        if (tab === 'activas') {
          const data = await api.get('/api/lines/telcel', {
            limit: LIMIT, offset: page * LIMIT, search: search || undefined
          })
          if (data.success) { setLines(data.data); setTotal(data.count || 0) }
        } else {
          const data = await api.get('/api/lines/telcel/bajas', { search: search || undefined })
          if (data.success) { setBajas(data.data); setTotal(data.count || 0) }
        }
      } catch (e) { console.error(e) }
      finally { setLoading(false) }
    }
    const t = setTimeout(load, search && tab === 'activas' ? 300 : 0)
    return () => clearTimeout(t)
  }, [tab, page, search])

  const darDeBaja = async (id) => {
    if (!confirm('¿Dar de baja esta línea? Dejará de aparecer en el CSV de pago.')) return
    setActionId(id)
    try {
      const r = await api.post(`/api/lines/telcel/${id}/baja`)
      if (r.success) {
        setLines(l => l.filter(l => l.id !== id))
        setTotal(n => Math.max(0, n - 1))
      }
    } catch (e) { alert('Error: ' + e.message) }
    finally { setActionId(null) }
  }

  const reactivar = async (id) => {
    setActionId(id)
    try {
      const r = await api.post(`/api/lines/telcel/${id}/reactivar`)
      if (r.success) {
        setBajas(l => l.filter(l => l.id !== id))
        setTotal(n => Math.max(0, n - 1))
      }
    } catch (e) { alert('Error: ' + e.message) }
    finally { setActionId(null) }
  }

  const exportarCSV = async () => {
    try { await api.download('/api/lines/telcel/export') }
    catch (e) { alert('Error exportando: ' + e.message) }
  }

  const agregarNumero = async (e) => {
    e.preventDefault()
    if (!form.phone.trim()) return
    setSaving(true)
    try {
      const r = await api.post('/api/lines/telcel', {
        phone: form.phone.trim(),
        carrier: form.carrier || null,
        amount: form.amount === '' ? null : Number(form.amount),
        date: form.date || null,
        client_name: form.client_name || null
      })
      if (r.success) {
        setShowAdd(false)
        setForm({ phone: '', carrier: 'TELCEL', amount: '', date: '', client_name: '' })
        const data = await api.get('/api/lines/telcel', { limit: LIMIT, offset: 0 })
        if (data.success) { setLines(data.data); setTotal(data.count || 0) }
      }
    } catch (err) { alert('Error: ' + err.message) }
    finally { setSaving(false) }
  }

  const pages = Math.ceil(total / LIMIT)

  const activasCols = [
    { key: 'phone',   label: 'Teléfono', mono: true },
    { key: 'carrier', label: 'Carrier'              },
    { key: 'amount',  label: 'Monto',    currency: true },
    { key: 'date',    label: 'Fecha'                },
    { key: 'client_name', label: 'Cliente'          },
  ]
  const bajasCols = [
    { key: 'phone',         label: 'Teléfono', mono: true },
    { key: 'carrier',       label: 'Carrier'              },
    { key: 'amount',        label: 'Monto',    currency: true },
    { key: 'date',          label: 'Fecha'                },
    { key: 'client_name',   label: 'Cliente (B2B)'        },
    { key: 'tracker_name',  label: 'Tracker',  badge: true },
    { key: 'tracker_client',label: 'Cliente Tracker'      },
    { key: 'deactivated_at',label: 'Fecha baja'           },
  ]

  const rows = tab === 'activas' ? lines : bajas
  const cols = tab === 'activas' ? activasCols : bajasCols

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Icon3D icon={Smartphone} color="sky" size="md" />
          Telcel B2B
        </h1>
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm">{total.toLocaleString()} registros</span>
          <button onClick={() => setShowAdd(true)}
            className="btn btn-success">
            <Plus className="w-4 h-4" /> Agregar número
          </button>
          {tab === 'activas' && (
            <button onClick={exportarCSV}
              className="btn btn-primary">
              <Download className="w-4 h-4" /> Exportar Excel
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-2xl bg-white/5 border border-white/10 w-fit">
        <button onClick={() => setTab('activas')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition ${
            tab === 'activas' ? 'bg-brand-gradient text-gray-950 shadow-glow-amber' : 'text-gray-400 hover:text-white'
          }`}>
          <Icon3D icon={ListChecks} color="emerald" size="sm" /> Activas (para pago)
        </button>
        <button onClick={() => setTab('bajas')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition ${
            tab === 'bajas' ? 'bg-brand-gradient text-gray-950 shadow-glow-amber' : 'text-gray-400 hover:text-white'
          }`}>
          <Icon3D icon={History} color="amber" size="sm" /> Historial de bajas
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder={tab === 'activas' ? 'Filtrar por teléfono...' : 'Filtrar por teléfono o cliente...'}
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0) }}
          className="w-full pl-9 pr-8 py-2 bg-gray-800 border border-gray-700 rounded-lg
                     text-white placeholder-gray-500 text-sm focus:border-amber-500 outline-none"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-7 h-7 text-amber-400 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            {tab === 'activas' ? 'Sin líneas activas' : 'Sin bajas registradas'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-white/10">
                  {cols.map(c => (
                    <th key={c.key} className="th">{c.label}</th>
                  ))}
                  <th className="th">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {rows.map((line, i) => (
                  <tr key={i} className="row-hover">
                    {cols.map(col => (
                      <td key={col.key} className="td">
                        <Cell col={col} value={line[col.key]} />
                      </td>
                    ))}
                    <td className="td">
                      {tab === 'activas' ? (
                        <button onClick={() => darDeBaja(line.id)} disabled={actionId === line.id}
                          className="btn btn-danger !py-1.5 !px-2.5 text-xs">
                          <Ban className="w-3.5 h-3.5" /> Dar de baja
                        </button>
                      ) : (
                        <button onClick={() => reactivar(line.id)} disabled={actionId === line.id}
                          className="btn btn-success !py-1.5 !px-2.5 text-xs">
                          <RotateCcw className="w-3.5 h-3.5" /> Reactivar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'activas' && <Pagination page={page} pages={pages} total={total} setPage={setPage} />}
      </div>

      {/* Modal: Agregar número */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShowAdd(false)}>
          <div className="card w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-white">Agregar número Telcel B2B</h3>
              <button type="button" onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={agregarNumero} className="space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Teléfono *</label>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="5512345678" required
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:border-amber-500 outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Carrier</label>
                  <input value={form.carrier} onChange={e => setForm(f => ({ ...f, carrier: e.target.value }))}
                    placeholder="TELCEL"
                    className="input" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Monto</label>
                  <input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    placeholder="0.00"
                    className="input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Fecha</label>
                  <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                    className="input" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Cliente</label>
                  <input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                    placeholder="Nombre del cliente"
                    className="input" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowAdd(false)}
                  className="btn btn-ghost">Cancelar</button>
                <button type="submit" disabled={saving}
                  className="btn btn-success">
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default function LineList() {
  const { type } = useParams()

  if (type === 'telcel') return <TelcelB2B />

  const [lines, setLines]   = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage]     = useState(0)
  const [total, setTotal]   = useState(0)

  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.toka
  const colors = colorMap[cfg.color] || colorMap.blue

  useEffect(() => { setPage(0); setSearch('') }, [type])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const data = await api.get(`/api/lines/${type}`, {
          limit: LIMIT, offset: page * LIMIT,
          search: search || undefined
        })
        if (data.success) { setLines(data.data); setTotal(data.count || 0) }
      } catch (e) { console.error(e) }
      finally { setLoading(false) }
    }
    const t = setTimeout(load, search ? 300 : 0)
    return () => clearTimeout(t)
  }, [type, page, search])

  const pages = Math.ceil(total / LIMIT)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className={`text-2xl font-bold text-white flex items-center gap-2`}>
          <Icon3D icon={Smartphone} color={TYPE_ICON_COLOR[type] || 'slate'} size="md" />
          {cfg.title}
        </h1>
        <span className="text-gray-400 text-sm">{total.toLocaleString()} registros</span>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Filtrar por teléfono, ICCID o cliente..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0) }}
          className="w-full pl-9 pr-8 py-2 bg-gray-800 border border-gray-700 rounded-lg
                     text-white placeholder-gray-500 text-sm focus:border-amber-500 outline-none"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-7 h-7 text-amber-400 animate-spin" />
          </div>
        ) : lines.length === 0 ? (
          <div className="text-center py-16 text-gray-500">Sin registros</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="border-b border-white/10">
                  {cfg.cols.map(c => (
                    <th key={c.key} className="th">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {lines.map((line, i) => (
                  <tr key={i} className="row-hover">
                    {cfg.cols.map(col => (
                      <td key={col.key} className="td">
                        <Cell col={col} value={line[col.key]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700 text-sm">
            <span className="text-gray-400">
              {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} de {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => p - 1)} disabled={page === 0}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-gray-400 min-w-[60px] text-center">{page + 1} / {pages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= pages - 1}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
